import { parseFromFile, workbookToBuffer, type Workbook, workbookSheetToJson, CellFormat } from '@/lib/excel';
import { ExcelComposer, type SectionConfig, type CellValue } from "@/lib/excel/composer";
import Decimal from "decimal.js";
import { safeParseDecimal } from "@/lib/api/common";
import { groupBy } from "@/lib/utils/groupBy";

export async function runReport(file: File) {
    const workbook = (await parseFromFile(file) as any).data.excelJsWorkbook;
    const composer = await GenerateMtmExcelComposer(workbook);

    return {
        buffer: await composer.getBuffer()
    };
}

// Month mappings for parsing delivery end dates (supports full names and abbreviations)
const MONTH_MAPPINGS = [
    { full: "January", abbreviations: ["Jan"] },
    { full: "February", abbreviations: ["Feb"] },
    { full: "March", abbreviations: ["Mar"] },
    { full: "April", abbreviations: ["Apr"] },
    { full: "May", abbreviations: ["May"] },
    { full: "June", abbreviations: ["Jun"] },
    { full: "July", abbreviations: ["Jul"] },
    { full: "August", abbreviations: ["Aug"] },
    { full: "September", abbreviations: ["Sep", "Sept"] },
    { full: "October", abbreviations: ["Oct"] },
    { full: "November", abbreviations: ["Nov"] },
    { full: "December", abbreviations: ["Dec"] },
] as const;

// Build lookup from MONTH_MAPPINGS
const monthNameToNumber = MONTH_MAPPINGS.reduce<Record<string, number>>((acc, month, index) => {
    acc[month.full.toLowerCase()] = index;
    month.abbreviations.forEach(abbr => {
        acc[abbr.toLowerCase()] = index;
    });
    return acc;
}, {});

/**
 * Parses a delivery end date string into a Date object.
 * Supports formats: MMM-YY, MMMM-YY, MMM-YYYY, MMMM-YYYY
 * (e.g., Dec-25, December-25, Dec-2025, December-2025)
 */
function parseDeliveryEndDate(dateStr: string): Date | null {
    if (!dateStr || dateStr.toLowerCase() === 'delinquent') {
        return null;
    }
    
    // Match patterns like "Dec-25", "December-25", "Dec-2025", "December-2025"
    const match = dateStr.match(/^(\w+)-(\d{2,4})$/i);
    if (!match) {
        return null;
    }
    
    const monthPart = match[1];
    const yearPart = match[2];
    
    if (!monthPart || !yearPart) {
        return null;
    }
    
    const monthNum = monthNameToNumber[monthPart.toLowerCase()];
    
    if (monthNum === undefined) {
        return null;
    }
    
    let year = parseInt(yearPart, 10);
    // Convert 2-digit year to 4-digit (50-99 -> 1950-1999, 00-49 -> 2000-2049)
    if (year < 100) {
        year = year >= 50 ? 1900 + year : 2000 + year;
    }
    
    return new Date(year, monthNum, 1);
}

/**
 * Sorts delivery end dates in ascending order (earliest first).
 * Special case: "Delinquent" is placed right after the earliest date.
 * Result: [Earliest, Delinquent, ...rest in ascending order]
 */
function sortDeliveryEndDates(dates: string[]): string[] {
    // Separate delinquent from regular dates
    const delinquent = dates.filter(d => /delinquent/i.test(d));
    const regularDates = dates.filter(d => !/delinquent/i.test(d));
    
    // Sort regular dates ascending
    const sorted = [...regularDates].sort((a, b) => {
        const dateA = parseDeliveryEndDate(a);
        const dateB = parseDeliveryEndDate(b);
        
        // If both can't be parsed, fall back to string comparison
        if (!dateA && !dateB) return a.localeCompare(b);
        // Unparseable dates go to the end
        if (!dateA) return 1;
        if (!dateB) return -1;
        
        // Sort ascending (earliest first)
        return dateA.getTime() - dateB.getTime();
    });
    
    // Insert delinquent after the first item (if there is one)
    const firstDate = sorted[0];
    if (delinquent.length > 0 && firstDate !== undefined) {
        return [firstDate, ...delinquent, ...sorted.slice(1)];
    }
    
    // If no regular dates, just return delinquent; if no delinquent, return sorted
    return [...sorted, ...delinquent];
}

export async function GenerateMtmExcelComposer(workbook: Workbook) {
    const worksheet = workbook.worksheets.find(item => /ctr mtm/i.test(item.name));
    const sheet = workbook.worksheets.length === 1 ? workbook.worksheets[0] : worksheet;
    const excelRows = workbookSheetToJson<MtMExcelReportRow>(sheet, { withHeaders: true })

    // Group rows first by "Commodity Name"
    const rowsByCommodityName = groupBy(excelRows, ["Commodity Name"] as const, { includeBlankValues: true });

    const composer = new ExcelComposer();

    // Process each commodity separately
    for (const commodityName of Object.keys(rowsByCommodityName).sort()) {
        const commodityRows = rowsByCommodityName[commodityName] as MtMExcelReportRow[];

        // Filter out rows where Market Zone is "HTA"
        const filteredRows = commodityRows.filter(row => row["Market Zone"] !== "HTA");

        // Skip if no rows remain after filtering
        if (filteredRows.length === 0) {
            continue;
        }

        // Create sheet for this commodity
        composer.createSheet(commodityName || "(blank)");

        // Process the commodity rows
        processCommodityRows(composer, filteredRows);
    }

    return composer;
}

function processCommodityRows(composer: ExcelComposer, rows: MtMExcelReportRow[]) {
    // Group rows by "Purchase/Sale" then by "Market Zone" then by "Delivery End Date"
    const rowsByCommodity = groupBy(rows, ["Purchase/Sale", "Market Zone", "Delivery End Date"] as const, { includeBlankValues: true });

    // Build aligned data structure
    // P/S should be before S/C, so purchase - sales qty.
    const purchaseSaleKeys = Object.keys(rowsByCommodity).sort();

    // Collect all Market Zones across all Purchase/Sale groups
    const allMarketZones = new Set<string>();
    for (const psKey of purchaseSaleKeys) {
        const psGroup = rowsByCommodity[psKey];
        if (psGroup) {
            Object.keys(psGroup).forEach(mz => allMarketZones.add(mz));
        }
    }
    const sortedMarketZones = Array.from(allMarketZones).sort();

    // Build structure: Market Zone -> Delivery End Dates -> Purchase/Sale -> rows
    type MarketZoneData = {
        deliveryEndDates: Map<string, Map<string, MtMExcelReportRow[]>>;
    };
    const marketZoneMap = new Map<string, MarketZoneData>();

    for (const mz of sortedMarketZones) {
        const mzData: MarketZoneData = { deliveryEndDates: new Map() };
        marketZoneMap.set(mz, mzData);

        for (const psKey of purchaseSaleKeys) {
            const psGroup = rowsByCommodity[psKey];
            if (psGroup && typeof psGroup === 'object' && psGroup[mz]) {
                const mzGroup = psGroup[mz];
                if (mzGroup && typeof mzGroup === 'object') {
                    for (const dedKey of Object.keys(mzGroup)) {
                        if (!mzData.deliveryEndDates.has(dedKey)) {
                            mzData.deliveryEndDates.set(dedKey, new Map());
                        }

                        const dedMap = mzData.deliveryEndDates.get(dedKey)!;
                        const rows = mzGroup[dedKey] as MtMExcelReportRow[];
                        if (Array.isArray(rows)) {
                            dedMap.set(psKey, rows);
                        }
                    }
                }
            }
        }
    }

    // Helper function to sum Open Qty from rows
    function sumOpenQty(rows: MtMExcelReportRow[]): number {
        return rows.reduce((sum, row) => {
            const qty = safeParseDecimal({})(String(row["Open Qty"] || "0"));
            return sum.plus(qty || 0);
        }, new Decimal(0)).toNumber();
    }

    // Build row data structure aligned by Market Zone
    type ReportRow = {
        type: 'marketZone' | 'deliveryEndDate';
        label: string;
        values: Map<string, number>; // Purchase/Sale -> sum
        marketZone: string;
        deliveryEndDate?: string;
    };

    const reportRows: ReportRow[] = [];

    for (const mz of sortedMarketZones) {
        const mzData = marketZoneMap.get(mz)!;
        const mzValues = new Map<string, number>();

        // Collect all Delivery End Dates for this Market Zone (sorted ascending, with "Delinquent" first)
        const deliveryEndDates = sortDeliveryEndDates(Array.from(mzData.deliveryEndDates.keys()));

        // First, calculate all Delivery End Date sums and accumulate Market Zone totals
        const dedRows: ReportRow[] = [];
        for (const ded of deliveryEndDates) {
            const dedMap = mzData.deliveryEndDates.get(ded)!;
            const dedValues = new Map<string, number>();

            for (const psKey of purchaseSaleKeys) {
                const rows = dedMap.get(psKey) || [];
                const sum = sumOpenQty(rows);
                dedValues.set(psKey, sum);
                // Add to Market Zone total
                mzValues.set(psKey,  (new Decimal(mzValues.get(psKey) || 0)).plus(sum).toNumber());
            }

            dedRows.push({
                type: 'deliveryEndDate',
                label: ded,
                values: dedValues,
                marketZone: mz,
                deliveryEndDate: ded
            });
        }

        // Add Market Zone row first, then its Delivery End Date rows
        reportRows.push({
            type: 'marketZone',
            label: mz,
            values: mzValues,
            marketZone: mz
        });
        reportRows.push(...dedRows);
    }

    // Build section data: each Purchase/Sale gets 2 columns (label, sum)
    // Labels are duplicated per group but must align by Market Zone
    const sectionData: Array<Array<CellValue>> = [];

    // Header row: for each Purchase/Sale group, add empty label column and "Sum {Purchase/Sale}"
    const headerRow: Array<CellValue> = [];
    for (const psKey of purchaseSaleKeys) {
        headerRow.push({ value: "", bold: true }); // Label column (empty in header)
        headerRow.push({ value: `Sum ${psKey}`, bold: true }); // Sum column
        headerRow.push({ value: "", bold: true }); // Space column
    }
    // Add Net column header
    headerRow.push({ value: "Net", bold: true });
    sectionData.push(headerRow);

    // Data rows: each Purchase/Sale group gets its own label column (aligned) and sum column
    for (const row of reportRows) {
        const dataRow: Array<CellValue> = [];

        // Calculate net value (sum of all Purchase/Sale values)
        const netValue = (() => {
            let netValue = new Decimal(row.values.get(purchaseSaleKeys[0] || "") || 0);
            for (const psKey of purchaseSaleKeys.slice(1)) {
                netValue = netValue.minus(row.values.get(psKey) || 0);
            }
            return netValue.toNumber();
        })();


        for (const psKey of purchaseSaleKeys) {
            const sumValue = row.values.get(psKey) || 0;
            const isMarketZone = row.type === 'marketZone';

            // Label column: Market Zone or Delivery End Date (same for all groups, must align)
            if (isMarketZone) {
                dataRow.push({
                    value: row.label,
                    bold: true
                });
            } else {
                // Delivery End Date row: indented label
                dataRow.push({
                    value: `  ${row.label}`
                });
            }

            // Sum column: value for this Purchase/Sale
            dataRow.push({
                value: sumValue,
                format: CellFormat.ACCOUNTING
            });
            dataRow.push({
                value: ""
            });
        }

        // Add Net column (only color-coded cells)
        const netCellFill = row.type === 'marketZone'
            ? { type: "pattern" as const, pattern: "solid" as const, fgColor: { argb: "FF00FF00" } } // Green for market zone
            : { type: "pattern" as const, pattern: "solid" as const, fgColor: { argb: "FFFFFF00" } }; // Yellow for others

        dataRow.push({
            value: netValue,
            format: CellFormat.ACCOUNTING,
            fill: netCellFill,
            bold: row.type === 'marketZone'
        });

        if (row.type === "marketZone") {
            sectionData.push([{ value: "" }]);
        }

        sectionData.push(dataRow);
    }

    // Build column styling: each Purchase/Sale group gets 3 columns (label: 30, sum: 20, space: 15)
    // Plus one net column at the end
    const columnStyling: Record<string, { width: number }> = {};
    for (let i = 0; i < purchaseSaleKeys.length; i++) {
        const labelCol = String.fromCharCode(65 + i * 3);
        const sumCol = String.fromCharCode(66 + i * 3);
        const spaceCol = String.fromCharCode(67 + i * 3);
        columnStyling[labelCol] = { width: 30 };
        columnStyling[sumCol] = { width: 20 };
        columnStyling[spaceCol] = { width: 15 };
    }
    // Add styling for Net column (last column after all Purchase/Sale groups)
    const netColIndex = purchaseSaleKeys.length * 3;
    const netCol = String.fromCharCode(65 + netColIndex);
    columnStyling[netCol] = { width: 20 };

    // Add section to composer
    composer.addSection({
        headers: [],
        data: sectionData,
        footerCalculations: [],
        styling: {
            columns: columnStyling
        }
    } as SectionConfig, { startRow: 1, startColumn: 1 });
}

export interface MtMExcelReportRow {
    "Purchase/Sale": string;
    "Trader Name": string;
    "Currency": string;
    "Location Id": string;
    "Location": string;
    "MTM Price Date": string;
    "MTM Commodity": string;
    "Price List No": string;
    "Commodity Id": string;
    "Commodity Name": string;
    "Price UOM": string;
    "Country": string;
    "Customer Id": string;
    "Customer": string;
    "Market Zone": string;
    "Price Type": string;
    "Delivery End Date": string;
    "Contract No": string;
    "Contract Type": string;
    "Open Qty": string;
    "Price Futures": string;
    "Price Basis": string;
    "Market Month": string;
    "Price": string;
    "MZ Adjustment": string;
    "MTM Basis": string;
    "MTM Price": string;
    "Market Price Futures": string;
    "Market Price Basis": string;
    "Gain/Loss / UOM Price/Futures": string;
    "Gain/Loss / UOM Basis": string;
    "Gain/Loss / UOM Net": string;
    "Gain/LossTotal": string;
    "Gain/Loss Price/Futures Total": string;
    "Gain/Loss Basis Total": string;
    "Confirm": string;
    "Contract Commodity Id": string;
    "Release Product": string;
    "Contract Date": string;
    "Scheduled Quantity": string;
    "Internal Contract Notes": string;

}

